import { useEffect, useState } from "react";
import { Alert, Button, Card, Col, Form, Row } from "react-bootstrap";
import i18n from "i18next";
import styles from "./StructureMapVisualEditor.module.css";

type JsonRecord = Record<string, any>;

interface StructureMapVisualEditorProps {
  content: string;
  onChange: (content: string) => void;
}

const modelModes = ["source", "queried", "target", "produced"];
const inputModes = ["source", "target"];
const groupTypeModes = ["none", "types", "type-and-types"];
const sourceListModes = ["first", "not_first", "last", "not_last", "only_one"];
const targetListModes = ["first", "share", "last", "collate"];
const transforms = [
  "create",
  "copy",
  "truncate",
  "escape",
  "cast",
  "append",
  "translate",
  "reference",
  "dateOp",
  "uuid",
  "pointer",
  "evaluate",
  "cc",
  "c",
  "qty",
  "id",
  "cp",
];
const parameterTypes = ["valueId", "valueString", "valueBoolean", "valueInteger", "valueDecimal"];

const asArray = (value: unknown): JsonRecord[] => (Array.isArray(value) ? (value as JsonRecord[]) : []);

const isBlank = (value: unknown) => value === undefined || value === null || String(value).trim() === "";

const clone = <T,>(value: T): T => JSON.parse(JSON.stringify(value)) as T;

function defaultRule(sourceContext = "src"): JsonRecord {
  return {
    name: "new-rule",
    source: [{ context: sourceContext }],
  };
}

function defaultGroup(): JsonRecord {
  return {
    name: "new-group",
    typeMode: "none",
    input: [
      { name: "src", mode: "source" },
      { name: "tgt", mode: "target" },
    ],
    rule: [defaultRule()],
  };
}

export function getStructureMapValidationErrors(map: JsonRecord): string[] {
  const errors: string[] = [];
  if (map.resourceType !== "StructureMap") errors.push("resourceType doit être « StructureMap ».");
  ["url", "name", "status"].forEach((field) => {
    if (isBlank(map[field])) errors.push(`${field} est obligatoire par FHIR R4.`);
  });
  if (map.status && !["draft", "active", "retired", "unknown"].includes(map.status)) {
    errors.push("status doit être draft, active, retired ou unknown.");
  }
  if (!asArray(map.group).length) errors.push("Au moins un groupe est obligatoire.");

  const checkRule = (rule: JsonRecord, path: string) => {
    if (isBlank(rule.name)) errors.push(`${path} : le nom de règle est obligatoire.`);
    if (!asArray(rule.source).length) errors.push(`${path} : au moins une source est obligatoire.`);
    asArray(rule.source).forEach((source, index) => {
      if (isBlank(source.context)) errors.push(`${path}, source ${index + 1} : context est obligatoire.`);
      if (source.min !== undefined && !Number.isInteger(source.min)) {
        errors.push(`${path}, source ${index + 1} : min doit être un entier.`);
      }
      if (source.listMode && !sourceListModes.includes(source.listMode)) {
        errors.push(`${path}, source ${index + 1} : listMode n'est pas une valeur FHIR valide.`);
      }
    });
    asArray(rule.target).forEach((target, index) => {
      const listModes = Array.isArray(target.listMode) ? target.listMode : [target.listMode].filter(Boolean);
      if (listModes.some((mode) => !targetListModes.includes(mode))) {
        errors.push(`${path}, cible ${index + 1} : listMode n'est pas une valeur FHIR valide.`);
      }
      asArray(target.parameter).forEach((parameter, parameterIndex) => {
        const valueKey = Object.keys(parameter).find((key) => key.startsWith("value"));
        if (!valueKey || isBlank(parameter[valueKey])) {
          errors.push(
            `${path}, cible ${index + 1}, paramètre ${parameterIndex + 1} : une valeur FHIR value[x] est obligatoire.`,
          );
        } else if (
          (valueKey === "valueBoolean" && typeof parameter[valueKey] !== "boolean") ||
          (valueKey === "valueInteger" && !Number.isInteger(parameter[valueKey])) ||
          (valueKey === "valueDecimal" && typeof parameter[valueKey] !== "number")
        ) {
          errors.push(
            `${path}, cible ${index + 1}, paramètre ${parameterIndex + 1} : ${valueKey} doit avoir le type JSON FHIR attendu.`,
          );
        }
      });
    });
    asArray(rule.dependent).forEach((dependent, index) => {
      if (isBlank(dependent.name) || !asArray(dependent.variable).length) {
        errors.push(`${path}, dépendance ${index + 1} : name et au moins une variable sont obligatoires.`);
      }
    });
    asArray(rule.rule).forEach((child, index) => checkRule(child, `${path}.${index + 1}`));
  };

  asArray(map.structure).forEach((structure, structureIndex) => {
    if (isBlank(structure.url) || !modelModes.includes(structure.mode)) {
      errors.push(`Modèle ${structureIndex + 1} : url et mode sont obligatoires.`);
    }
  });

  asArray(map.group).forEach((group, index) => {
    const path = `Groupe ${index + 1}`;
    if (isBlank(group.name)) errors.push(`${path} : name est obligatoire.`);
    if (!groupTypeModes.includes(group.typeMode)) errors.push(`${path} : typeMode est obligatoire.`);
    if (!asArray(group.input).length) errors.push(`${path} : au moins une entrée est obligatoire.`);
    asArray(group.input).forEach((input, inputIndex) => {
      if (isBlank(input.name) || !inputModes.includes(input.mode)) {
        errors.push(`${path}, entrée ${inputIndex + 1} : name et mode sont obligatoires.`);
      }
    });
    if (!asArray(group.rule).length) errors.push(`${path} : au moins une règle est obligatoire.`);
    asArray(group.rule).forEach((rule, ruleIndex) => checkRule(rule, `${path}, règle ${ruleIndex + 1}`));
  });
  return errors;
}

function parameterValue(key: string, value: string): string | number | boolean {
  if (value === "") return "";
  if (key === "valueBoolean") return value === "true";
  if (key === "valueInteger" || key === "valueDecimal") return Number(value);
  return value;
}

interface FieldProps {
  label: string;
  value?: unknown;
  placeholder?: string;
  required?: boolean;
  onChange: (value: string) => void;
}

function TextField({ label, value, placeholder, required, onChange }: FieldProps) {
  return (
    <Form.Group className={styles.field}>
      <Form.Label>
        {label}
        {required ? " *" : ""}
      </Form.Label>
      <Form.Control
        size="sm"
        value={value === undefined || value === null ? "" : String(value)}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
      />
    </Form.Group>
  );
}

function SelectField({ label, value, options, onChange }: FieldProps & { options: string[] }) {
  return (
    <Form.Group className={styles.field}>
      <Form.Label>{label}</Form.Label>
      <Form.Select
        size="sm"
        value={value === undefined || value === null ? "" : String(value)}
        onChange={(event) => onChange(event.target.value)}
      >
        <option value="">—</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </Form.Select>
    </Form.Group>
  );
}

function removeEmptyField(record: JsonRecord, field: string, value: string) {
  if (value.trim()) record[field] = value;
  else delete record[field];
}

export default function StructureMapVisualEditor({ content, onChange }: StructureMapVisualEditorProps) {
  const [map, setMap] = useState<JsonRecord | undefined>();
  const [parseError, setParseError] = useState<string>();

  useEffect(() => {
    try {
      const parsed = JSON.parse(content) as JsonRecord;
      if (!parsed || Array.isArray(parsed)) throw new Error("La racine doit être un objet JSON.");
      setMap(parsed);
      setParseError(undefined);
    } catch (error) {
      setParseError(error instanceof Error ? error.message : "JSON invalide");
    }
  }, [content]);

  const commit = (change: (draft: JsonRecord) => void) => {
    if (!map) return;
    const next = clone(map);
    change(next);
    setMap(next);
    onChange(JSON.stringify(next, null, "\t"));
  };

  const t = (key: string, options?: Record<string, unknown>) => i18n.t(`structureMap.visual.${key}`, options);

  const editField = (record: JsonRecord, field: string, value: string, required = false) => {
    if (required) record[field] = value;
    else removeEmptyField(record, field, value);
  };

  const renderParameter = (
    parameter: JsonRecord,
    onUpdate: (change: (draft: JsonRecord) => void) => void,
    onRemove: () => void,
    key: number,
  ) => {
    const existingKey = Object.keys(parameter).find((item) => item.startsWith("value")) ?? "valueId";
    return (
      <div className={styles.parameter} key={key}>
        <SelectField
          label={t("field.type")}
          value={existingKey}
          options={parameterTypes}
          onChange={(value) =>
            onUpdate((draft) => {
              Object.keys(draft)
                .filter((item) => item.startsWith("value"))
                .forEach((item) => delete draft[item]);
              draft[value] = "";
            })
          }
        />
        <TextField
          label={t("field.value")}
          value={parameter[existingKey]}
          onChange={(value) =>
            onUpdate((draft) => {
              draft[existingKey] = parameterValue(existingKey, value);
            })
          }
        />
        <Button variant="outline-danger" size="sm" onClick={onRemove}>
          {t("remove")}
        </Button>
      </div>
    );
  };

  const renderSource = (
    source: JsonRecord,
    onUpdate: (change: (draft: JsonRecord) => void) => void,
    onRemove: () => void,
    key: number,
  ) => (
    <Card className={styles.lineCard} key={key} body>
      <div className={styles.lineHeader}>
        <strong>{t("source", { index: key + 1 })}</strong>
        <Button variant="outline-danger" size="sm" onClick={onRemove}>
          {t("remove")}
        </Button>
      </div>
      <Row>
        <Col md={3}>
          <TextField
            label={t("field.context")}
            required
            value={source.context}
            onChange={(value) => onUpdate((draft) => editField(draft, "context", value, true))}
          />
        </Col>
        <Col md={3}>
          <TextField
            label={t("field.element")}
            value={source.element}
            placeholder="name.family"
            onChange={(value) => onUpdate((draft) => editField(draft, "element", value))}
          />
        </Col>
        <Col md={2}>
          <TextField
            label={t("field.variable")}
            value={source.variable}
            onChange={(value) => onUpdate((draft) => editField(draft, "variable", value))}
          />
        </Col>
        <Col md={2}>
          <TextField
            label={t("field.type")}
            value={source.type}
            onChange={(value) => onUpdate((draft) => editField(draft, "type", value))}
          />
        </Col>
        <Col md={2}>
          <SelectField
            label={t("field.list")}
            value={source.listMode}
            options={sourceListModes}
            onChange={(value) => onUpdate((draft) => editField(draft, "listMode", value))}
          />
        </Col>
      </Row>
      <Row>
        <Col md={4}>
          <TextField
            label={t("field.condition")}
            value={source.condition}
            onChange={(value) => onUpdate((draft) => editField(draft, "condition", value))}
          />
        </Col>
        <Col md={4}>
          <TextField
            label={t("field.check")}
            value={source.check}
            onChange={(value) => onUpdate((draft) => editField(draft, "check", value))}
          />
        </Col>
        <Col md={2}>
          <TextField
            label={t("field.min")}
            value={source.min}
            onChange={(value) =>
              onUpdate((draft) => {
                if (value === "") delete draft.min;
                else draft.min = Number(value);
              })
            }
          />
        </Col>
        <Col md={2}>
          <TextField
            label={t("field.max")}
            value={source.max}
            onChange={(value) => onUpdate((draft) => editField(draft, "max", value))}
          />
        </Col>
      </Row>
    </Card>
  );

  const renderTarget = (
    target: JsonRecord,
    onUpdate: (change: (draft: JsonRecord) => void) => void,
    onRemove: () => void,
    key: number,
  ) => (
    <Card className={styles.lineCard} key={key} body>
      <div className={styles.lineHeader}>
        <strong>{t("target", { index: key + 1 })}</strong>
        <Button variant="outline-danger" size="sm" onClick={onRemove}>
          {t("remove")}
        </Button>
      </div>
      <Row>
        <Col md={3}>
          <TextField
            label={t("field.context")}
            value={target.context}
            onChange={(value) => onUpdate((draft) => editField(draft, "context", value))}
          />
        </Col>
        <Col md={2}>
          <SelectField
            label={t("field.contextType")}
            value={target.contextType}
            options={["type", "variable"]}
            onChange={(value) => onUpdate((draft) => editField(draft, "contextType", value))}
          />
        </Col>
        <Col md={3}>
          <TextField
            label={t("field.element")}
            value={target.element}
            placeholder="name.family"
            onChange={(value) => onUpdate((draft) => editField(draft, "element", value))}
          />
        </Col>
        <Col md={2}>
          <TextField
            label={t("field.variable")}
            value={target.variable}
            onChange={(value) => onUpdate((draft) => editField(draft, "variable", value))}
          />
        </Col>
        <Col md={2}>
          <SelectField
            label={t("field.transform")}
            value={target.transform}
            options={transforms}
            onChange={(value) => onUpdate((draft) => editField(draft, "transform", value))}
          />
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <TextField
            label={t("field.listModes")}
            value={Array.isArray(target.listMode) ? target.listMode.join(", ") : target.listMode}
            placeholder={targetListModes.join(", ")}
            onChange={(value) =>
              onUpdate((draft) => {
                const values = value
                  .split(",")
                  .map((item) => item.trim())
                  .filter(Boolean);
                if (values.length) draft.listMode = values;
                else delete draft.listMode;
              })
            }
          />
        </Col>
        <Col md={3}>
          <TextField
            label={t("field.listReference")}
            value={target.listRuleId}
            onChange={(value) => onUpdate((draft) => editField(draft, "listRuleId", value))}
          />
        </Col>
      </Row>
      <div className={styles.parameterList}>
        {asArray(target.parameter).map((parameter, parameterIndex) =>
          renderParameter(
            parameter,
            (change) => onUpdate((draft) => change(draft.parameter[parameterIndex])),
            () => onUpdate((draft) => draft.parameter.splice(parameterIndex, 1)),
            parameterIndex,
          ),
        )}
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={() =>
            onUpdate((draft) => {
              (draft.parameter ??= []).push({ valueId: "" });
            })
          }
        >
          {t("addParameter")}
        </Button>
      </div>
    </Card>
  );

  const renderRule = (rule: JsonRecord, groupIndex: number, path: number[], depth: number): React.ReactNode => {
    const getRule = (draft: JsonRecord): JsonRecord =>
      path.reduce(
        (current, index, level) => (level === 0 ? current.group[groupIndex].rule[index] : current.rule[index]),
        draft,
      );
    const updateRule = (change: (draft: JsonRecord) => void) => commit((draft) => change(getRule(draft)));
    const sourceContext = asArray(rule.source)[0]?.context || "src";
    return (
      <Card key={path.join("-")} className={styles.ruleCard} body style={{ marginLeft: `${depth * 18}px` }}>
        <div className={styles.ruleHeader}>
          <span>{t("rule", { index: path.map((item) => item + 1).join(".") })}</span>
          <Button
            variant="outline-danger"
            size="sm"
            onClick={() =>
              commit((draft) => {
                if (path.length === 1) draft.group[groupIndex].rule.splice(path[0], 1);
                else {
                  const parent = path
                    .slice(0, -1)
                    .reduce(
                      (current: JsonRecord, index, level) =>
                        level === 0 ? current.group[groupIndex].rule[index] : current.rule[index],
                      draft,
                    );
                  parent.rule.splice(path[path.length - 1], 1);
                }
              })
            }
          >
            {t("deleteRule")}
          </Button>
        </div>
        <Row>
          <Col md={4}>
            <TextField
              label={t("field.name")}
              required
              value={rule.name}
              onChange={(value) => updateRule((draft) => editField(draft, "name", value, true))}
            />
          </Col>
          <Col md={8}>
            <TextField
              label={t("field.documentation")}
              value={rule.documentation}
              onChange={(value) => updateRule((draft) => editField(draft, "documentation", value))}
            />
          </Col>
        </Row>
        <div className={styles.sectionLabel}>{t("sources")}</div>
        {asArray(rule.source).map((source, sourceIndex) =>
          renderSource(
            source,
            (change) => updateRule((draft) => change(draft.source[sourceIndex])),
            () => updateRule((draft) => draft.source.splice(sourceIndex, 1)),
            sourceIndex,
          ),
        )}
        <Button
          variant="outline-primary"
          size="sm"
          onClick={() =>
            updateRule((draft) => {
              (draft.source ??= []).push({ context: sourceContext });
            })
          }
        >
          {t("addSource")}
        </Button>
        <div className={styles.sectionLabel}>{t("targets")}</div>
        {asArray(rule.target).map((target, targetIndex) =>
          renderTarget(
            target,
            (change) => updateRule((draft) => change(draft.target[targetIndex])),
            () => updateRule((draft) => draft.target.splice(targetIndex, 1)),
            targetIndex,
          ),
        )}
        <Button
          variant="outline-primary"
          size="sm"
          onClick={() =>
            updateRule((draft) => {
              (draft.target ??= []).push({});
            })
          }
        >
          {t("addTarget")}
        </Button>
        <div className={styles.sectionLabel}>{t("dependencies")}</div>
        {asArray(rule.dependent).map((dependent, dependentIndex) => (
          <Row className={styles.compactRow} key={dependentIndex}>
            <Col md={5}>
              <TextField
                label={t("field.groupOrRuleName")}
                required
                value={dependent.name}
                onChange={(value) =>
                  updateRule((draft) => editField(draft.dependent[dependentIndex], "name", value, true))
                }
              />
            </Col>
            <Col md={5}>
              <TextField
                label={t("field.variables")}
                value={asArray(dependent.variable).join(", ")}
                onChange={(value) =>
                  updateRule((draft) => {
                    const variables = value
                      .split(",")
                      .map((item) => item.trim())
                      .filter(Boolean);
                    if (variables.length) draft.dependent[dependentIndex].variable = variables;
                    else delete draft.dependent[dependentIndex].variable;
                  })
                }
              />
            </Col>
            <Col md={2} className={styles.deleteColumn}>
              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => updateRule((draft) => draft.dependent.splice(dependentIndex, 1))}
              >
                ×
              </Button>
            </Col>
          </Row>
        ))}
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={() =>
            updateRule((draft) => {
              (draft.dependent ??= []).push({ name: "", variable: [] });
            })
          }
        >
          {t("addDependency")}
        </Button>
        <div className={styles.sectionLabel}>{t("nestedRules")}</div>
        {asArray(rule.rule).map((child, childIndex) => renderRule(child, groupIndex, [...path, childIndex], depth + 1))}
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={() =>
            updateRule((draft) => {
              (draft.rule ??= []).push(defaultRule(sourceContext));
            })
          }
        >
          {t("addNestedRule")}
        </Button>
      </Card>
    );
  };

  if (parseError) return <Alert variant="warning">{t("invalidJson", { error: parseError })}</Alert>;
  if (!map) return null;
  const errors = getStructureMapValidationErrors(map);

  return (
    <div className={styles.editor}>
      <Alert variant={errors.length ? "warning" : "success"} className={styles.validation}>
        <strong>{errors.length ? t("validation", { count: errors.length }) : t("valid")}</strong>
        {errors.length > 0 && (
          <ul>
            {errors.map((error) => (
              <li key={error}>{error}</li>
            ))}
          </ul>
        )}
      </Alert>

      <Card className={styles.rootCard} body>
        <h3>{t("definition")}</h3>
        <Row>
          <Col md={4}>
            <TextField
              label={t("field.canonicalUrl")}
              required
              value={map.url}
              onChange={(value) => commit((draft) => editField(draft, "url", value, true))}
            />
          </Col>
          <Col md={3}>
            <TextField
              label={t("field.technicalName")}
              required
              value={map.name}
              onChange={(value) => commit((draft) => editField(draft, "name", value, true))}
            />
          </Col>
          <Col md={3}>
            <TextField
              label={t("field.title")}
              value={map.title}
              onChange={(value) => commit((draft) => editField(draft, "title", value))}
            />
          </Col>
          <Col md={2}>
            <SelectField
              label={t("field.status")}
              value={map.status}
              options={["draft", "active", "retired", "unknown"]}
              onChange={(value) => commit((draft) => editField(draft, "status", value, true))}
            />
          </Col>
        </Row>
        <TextField
          label={t("field.description")}
          value={map.description}
          onChange={(value) => commit((draft) => editField(draft, "description", value))}
        />
      </Card>

      <Card className={styles.rootCard} body>
        <div className={styles.cardTitle}>
          <h3>{t("modelsAndImports")}</h3>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() =>
              commit((draft) => {
                (draft.structure ??= []).push({ url: "", mode: "source" });
              })
            }
          >
            {t("addModel")}
          </Button>
        </div>
        {asArray(map.structure).map((structure, index) => (
          <Row className={styles.compactRow} key={index}>
            <Col md={5}>
              <TextField
                label={t("field.modelUrl")}
                required
                value={structure.url}
                onChange={(value) => commit((draft) => editField(draft.structure[index], "url", value, true))}
              />
            </Col>
            <Col md={2}>
              <SelectField
                label={t("field.mode")}
                value={structure.mode}
                options={modelModes}
                onChange={(value) => commit((draft) => editField(draft.structure[index], "mode", value, true))}
              />
            </Col>
            <Col md={2}>
              <TextField
                label={t("field.alias")}
                value={structure.alias}
                onChange={(value) => commit((draft) => editField(draft.structure[index], "alias", value))}
              />
            </Col>
            <Col md={2}>
              <TextField
                label={t("field.documentation")}
                value={structure.documentation}
                onChange={(value) => commit((draft) => editField(draft.structure[index], "documentation", value))}
              />
            </Col>
            <Col md={1} className={styles.deleteColumn}>
              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => commit((draft) => draft.structure.splice(index, 1))}
              >
                ×
              </Button>
            </Col>
          </Row>
        ))}
        <div className={styles.imports}>
          <strong>{t("imports")}</strong>
          {asArray(map.import).map((url, index) => (
            <div key={index} className={styles.importLine}>
              <Form.Control
                size="sm"
                value={url as any}
                onChange={(event) =>
                  commit((draft) => {
                    draft.import[index] = event.target.value;
                  })
                }
              />
              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => commit((draft) => draft.import.splice(index, 1))}
              >
                ×
              </Button>
            </div>
          ))}
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() =>
              commit((draft) => {
                (draft.import ??= []).push("");
              })
            }
          >
            {t("addImport")}
          </Button>
        </div>
      </Card>

      <div className={styles.groupsHeader}>
        <h3>{t("groups")}</h3>
        <Button
          onClick={() =>
            commit((draft) => {
              (draft.group ??= []).push(defaultGroup());
            })
          }
        >
          {t("addGroup")}
        </Button>
      </div>
      {asArray(map.group).map((group, groupIndex) => (
        <Card key={groupIndex} className={styles.groupCard} body>
          <div className={styles.cardTitle}>
            <h4>{t("group", { index: groupIndex + 1 })}</h4>
            <Button
              variant="outline-danger"
              size="sm"
              onClick={() => commit((draft) => draft.group.splice(groupIndex, 1))}
            >
              {t("deleteGroup")}
            </Button>
          </div>
          <Row>
            <Col md={4}>
              <TextField
                label={t("field.name")}
                required
                value={group.name}
                onChange={(value) => commit((draft) => editField(draft.group[groupIndex], "name", value, true))}
              />
            </Col>
            <Col md={3}>
              <SelectField
                label={t("field.typeMode")}
                value={group.typeMode}
                options={groupTypeModes}
                onChange={(value) => commit((draft) => editField(draft.group[groupIndex], "typeMode", value, true))}
              />
            </Col>
            <Col md={2}>
              <TextField
                label={t("field.extends")}
                value={group.extends}
                onChange={(value) => commit((draft) => editField(draft.group[groupIndex], "extends", value))}
              />
            </Col>
            <Col md={3}>
              <TextField
                label={t("field.documentation")}
                value={group.documentation}
                onChange={(value) => commit((draft) => editField(draft.group[groupIndex], "documentation", value))}
              />
            </Col>
          </Row>
          <div className={styles.sectionLabel}>{t("inputs")}</div>
          {asArray(group.input).map((input, inputIndex) => (
            <Row className={styles.compactRow} key={inputIndex}>
              <Col md={3}>
                <TextField
                  label={t("field.name")}
                  required
                  value={input.name}
                  onChange={(value) =>
                    commit((draft) => editField(draft.group[groupIndex].input[inputIndex], "name", value, true))
                  }
                />
              </Col>
              <Col md={3}>
                <TextField
                  label={t("field.type")}
                  value={input.type}
                  placeholder="Patient, CSV, HL7v2…"
                  onChange={(value) =>
                    commit((draft) => editField(draft.group[groupIndex].input[inputIndex], "type", value))
                  }
                />
              </Col>
              <Col md={3}>
                <SelectField
                  label={t("field.mode")}
                  value={input.mode}
                  options={inputModes}
                  onChange={(value) =>
                    commit((draft) => editField(draft.group[groupIndex].input[inputIndex], "mode", value, true))
                  }
                />
              </Col>
              <Col md={2}>
                <TextField
                  label={t("field.documentation")}
                  value={input.documentation}
                  onChange={(value) =>
                    commit((draft) => editField(draft.group[groupIndex].input[inputIndex], "documentation", value))
                  }
                />
              </Col>
              <Col md={1} className={styles.deleteColumn}>
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => commit((draft) => draft.group[groupIndex].input.splice(inputIndex, 1))}
                >
                  ×
                </Button>
              </Col>
            </Row>
          ))}
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() =>
              commit((draft) => {
                (draft.group[groupIndex].input ??= []).push({ name: "", mode: "source" });
              })
            }
          >
            {t("addInput")}
          </Button>
          <div className={styles.sectionLabel}>{t("rules")}</div>
          {asArray(group.rule).map((rule, ruleIndex) => renderRule(rule, groupIndex, [ruleIndex], 0))}
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() =>
              commit((draft) => {
                const source =
                  asArray(draft.group[groupIndex].input).find((input) => input.mode === "source")?.name || "src";
                (draft.group[groupIndex].rule ??= []).push(defaultRule(source));
              })
            }
          >
            {t("addRule")}
          </Button>
        </Card>
      ))}
    </div>
  );
}
